import {
  CButton, CCardBody, CCol, CContainer, CForm, CFormInput, CFormLabel, CFormSelect, CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { getInterest } from "src/api/allApis";
import { successAlert } from "src/utils/Swal";
import SearchableProductSelect from "src/components/SearchableProductSelect/SearchableProductSelect";
import { clamp } from "src/services/helpers";
import { buildReducingEmiSchedule, toNumber } from "src/services/emi-calculation";

const RegistrationForm = (props) => {
  const [submission, setSubmission] = useState(false);
  const [interset, setInterest] = useState([]);
  const [mainData, setMainData] = useState({
    file_charges: "",
    loanType: "",
    downPaymentAmount: "",
    loanAmountRequsted: "",
    ...props?.formData,
  });
  const [downPaymentInput, setDownPaymentInput] = useState(
    mainData.downPaymentAmount !== undefined && mainData.downPaymentAmount !== null
      ? String(mainData.downPaymentAmount)
      : ""
  );
  const file_charges = mainData.file_charges !== undefined && mainData.file_charges !== null
    ? String(mainData.file_charges)
    : ""
  console.log("Initial mainData in RegistrationForm:", mainData);
  //Emi Calculation
  const [emiResult, setEmiResult] = useState({ emi: 0, schedule: [] });
  const [showEmiTable, setShowEmiTable] = useState(false);

  useEffect(() => {
    fetchInterest();
  }, []);

  useEffect(() => {
    if (submission === true) successAlert("Success", "New Customer has been Created");
  }, [submission]);

  useEffect(() => {
    if (mainData.downPaymentAmount !== undefined && mainData.downPaymentAmount !== null) {
      setDownPaymentInput(String(mainData.downPaymentAmount));
    } else {
      setDownPaymentInput("");
    }
  }, [mainData.downPaymentAmount, mainData.loanType]);

  const fetchInterest = async () => {
    const res = await getInterest({ schemaName: sessionStorage.getItem("schemaName") });
    setInterest(res?.data || []);
  };

  const selectedProduct = useMemo(() => {
    return interset?.find(
      (item) => String(item?.id) === String(mainData?.loanType)
    );
  }, [interset, mainData?.loanType]);

  const mobilePrice = useMemo(() => {
    return Number(selectedProduct?.price ?? 0);
  }, [selectedProduct]);

  const minDownPaymentAmount = useMemo(() => {
    if (!selectedProduct) return 0;
    const pct = Number(selectedProduct?.down_payment ?? 0);
    return Math.round((mobilePrice * pct) / 100);
  }, [selectedProduct, mobilePrice]);

  const onChangeValue = (e) => {
    const { name, value } = e.target;

    if (name === "loanType") {
      const product = interset.find((i) => String(i.id) === String(value));
      const price = Number(product?.price ?? 0);
      const pct = Number(product?.down_payment ?? 0);
      const minDp = Math.round((price * pct) / 100);
      const dp = clamp(minDp, 0, price);
      const requested = price - dp;
      const file_charges = Number(product?.file_charges ?? 0);

      setMainData((prev) => ({
        ...prev,
        file_charges,
        loanType: value,
        downPaymentAmount: dp,
        loanAmountRequsted: requested,
      }));
      return;
    }

    if (name === "downPaymentAmount") {
      if (!selectedProduct) return;
      const price = Number(selectedProduct?.price ?? 0);
      const pct = Number(selectedProduct?.down_payment ?? 0);
      const minDp = Math.round((price * pct) / 100);
      const dpInput = Number(value || 0);
      const dp = clamp(dpInput, minDp, price);
      const requested = price - dp;
      const file_charges = Number(selectedProduct?.file_charges ?? 0);
      setMainData((prev) => ({
        ...prev,
        downPaymentAmount: dp,
        loanAmountRequsted: requested,
        file_charges: file_charges,
      }));
      return;
    }

    setMainData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (values) => {
    values.preventDefault();
    props.updateFormData("registration", mainData);
    props.setKey("address");
    setSubmission(true);
  };

  const {
    firstName, grandFatherName, martialStatus, phoneNumber, loanAmountRequsted, gender, dob, imei1, imei2, fatherName,
    loanType, loanTerm } = mainData;

  const selectedAnnualRate = useMemo(() => {
    return toNumber(selectedProduct?.rate ?? selectedProduct?.rate ?? 0);
  }, [selectedProduct]);

  useEffect(() => {
    setShowEmiTable(false);
    setEmiResult({ emi: 0, schedule: [] });
  }, [mainData.loanType, mainData.loanTerm, mainData.loanAmountRequsted]);

  const handleShowEmi = () => {
    const principal = toNumber(mainData.loanAmountRequsted) + toNumber(mainData.file_charges);
    const months = toNumber(mainData.loanTerm);
    const annualRate = selectedAnnualRate;

    const result = buildReducingEmiSchedule({ principal, annualRate, months });

    setEmiResult(result);
    setShowEmiTable(true);
  };

  return (
    <>
      <CCardBody className="custom-card">
        <CContainer className="">
          <CForm onSubmit={(event) => handleSubmit(event)} autoComplete="off">
            <CRow >
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="firstName" className="custom-form-label">
                  Full Name<span className="red-star">{` `}*</span>
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={firstName}
                  onChange={onChangeValue}
                  className="custom-form-input"
                  placeholder="Full Name As Per Aadhar"
                />
              </CCol>
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="phoneNumber" className="custom-form-label">
                  Phone Number<span className="red-star">{` `}*</span>
                </CFormLabel>
                <CFormInput
                  type="number"
                  id="phoneNumber"
                  maxLength={10}
                  placeholder="Phone Number"
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => {
                    const v = String(e.target.value || "")
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    onChangeValue({
                      target: { name: "phoneNumber", value: v },
                    });
                  }}
                  className="custom-form-input"
                />
              </CCol>
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="planType" className="custom-form-label">
                  Date of Birth
                  <span className="red-star">{` `}*</span>
                </CFormLabel>
                <CFormInput
                  type="date"
                  id="dob"
                  name="dob"
                  value={dob}
                  onChange={onChangeValue}
                  className="custom-form-input"
                />
              </CCol>
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="gender" className="custom-form-label">
                  Gender
                  <span className="red-star">{` `}*</span>
                </CFormLabel>

                <CFormSelect
                  className="custom-select custom-form-input"
                  id="gender"
                  name="gender"
                  value={gender}
                  onChange={onChangeValue}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </CFormSelect>
              </CCol>
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="father " className="custom-form-label">Father/Husband Name</CFormLabel>
                <CFormInput
                  type="text"
                  id="fatherName"
                  placeholder="Father/Husband Name"
                  name="fatherName"
                  value={fatherName}
                  onChange={onChangeValue}
                  className="custom-form-input"
                />
              </CCol>
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="grandFatherName" className="custom-form-label">
                  Grand Father Name
                  <span className="red-star">{` `}*</span>
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="grandFatherName"
                  placeholder="grandFatherName / father-in-Law"
                  name="grandFatherName"
                  value={grandFatherName}
                  onChange={onChangeValue}
                  required
                  className="custom-form-input"
                />
              </CCol>
              <CCol className="pt-20" xs={6}>
                <CFormLabel htmlFor="martialStatus" className="custom-form-label">
                  Marital Status
                  <span className="red-star">{` `}*</span>
                </CFormLabel>
                <CFormSelect
                  className="custom-select custom-form-input"
                  id="gender"
                  name="martialStatus"
                  type="select"
                  value={martialStatus}
                  onChange={onChangeValue}
                >
                  <option value="">Select Marital Status{martialStatus}</option>
                  <option value="married">Married</option>
                  <option value="unMarried">Unmarried</option>
                </CFormSelect>
              </CCol>
              {_.isEmpty(props?.customer) && (
                <>
                  <SearchableProductSelect
                    products={interset}
                    value={loanType}
                    onChange={(value) =>
                      onChangeValue({
                        target: {
                          name: "loanType",
                          value,
                        },
                      })
                    }
                    label="Select Product"
                  />
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel className="custom-form-label">
                      Mobile Price (₹)<span className="red-star"> *</span>
                    </CFormLabel>
                    <CFormInput
                      type="number"
                      value={mobilePrice || ""}
                      readOnly
                      className="custom-form-input"
                    />
                  </CCol>
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel className="custom-form-label">
                      File Charges (₹)<span className="red-star"> *</span>
                    </CFormLabel>
                    <CFormInput
                      type="number"
                      value={file_charges || ""}
                      readOnly
                      className="custom-form-input"
                    />
                  </CCol>
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel
                      htmlFor="downPaymentAmount"
                      className="custom-form-label"
                    >
                      Down Payment (₹)<span className="red-star"> *</span>
                    </CFormLabel>
                    <CFormInput
                      type="text"
                      id="downPaymentAmount"
                      name="downPaymentAmount"
                      value={downPaymentInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "" || /^[0-9]*$/.test(raw)) {
                          setDownPaymentInput(raw);
                        }
                      }}
                      onBlur={() => {
                        const parsed = Number(downPaymentInput || 0);
                        const price = Number(selectedProduct?.price ?? 0);
                        const pct = Number(selectedProduct?.down_payment ?? 0);
                        const minDp = Math.round((price * pct) / 100);
                        const dp = clamp(parsed, minDp, price);
                        setMainData((prev) => ({
                          ...prev,
                          downPaymentAmount: dp,
                          loanAmountRequsted: price - dp,
                        }));
                        setDownPaymentInput(String(dp));
                      }}
                      placeholder={selectedProduct ? `Min ₹${minDownPaymentAmount}` : ""}
                      disabled={!selectedProduct}
                      required
                      className="custom-form-input"
                    />
                  </CCol>
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel
                      htmlFor="loanAmountRequsted"
                      className="custom-form-label"
                    >
                      Requested Loan Amount<span className="red-star"> *</span>
                    </CFormLabel>
                    <CFormInput
                      type="number"
                      id="loanAmountRequsted"
                      placeholder="Requested Loan Amount"
                      name="loanAmountRequsted"
                      value={loanAmountRequsted || ""}
                      readOnly
                      required
                      className="custom-form-input"
                    />
                  </CCol>
                </>
              )}

              {_.isEmpty(props?.customer) && (
                <>
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel htmlFor="loanType" className="custom-form-label">
                      Loan Type
                      <span className="red-star">{` `}*</span>
                    </CFormLabel>
                    <CFormSelect
                      className="custom-select custom-form-input"
                      id="loanType"
                      name="loanType"
                      type="select"
                      value={loanType}
                      onChange={onChangeValue}
                      required
                      disabled
                    >
                      <option value="">Select Loan Type</option>
                      {interset?.map((item, i) => (
                        <option key={i} value={item?.id}>
                          {item?.loanType?.toUpperCase()}
                        </option>
                      ))}
                    </CFormSelect>
                  </CCol>

                  <CCol className="pt-20" xs={6}>
                    <CFormLabel htmlFor="loanTerm" className="custom-form-label">
                      Term [in Months]
                      <span className="red-star">{` `}*</span>
                    </CFormLabel>
                    <CFormInput
                      type="number"
                      id="loanTerm"
                      placeholder="Loan Term"
                      name="loanTerm"
                      value={loanTerm}
                      onChange={onChangeValue}
                      required
                      className="custom-form-input"
                    />
                  </CCol>
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel htmlFor="IMEI" className="custom-form-label">
                      Customer New Mobile IMEI  No<span className="red-star">{` `}*</span>
                    </CFormLabel>
                    <CFormInput
                      type="number"
                      id="imei"
                      placeholder="Enter Your IMEI Number "
                      name="imei1"
                      value={imei1}
                      onChange={onChangeValue}
                      required
                      className="custom-form-input"
                    />
                  </CCol>
                  <CCol className="pt-20" xs={6}>
                    <CFormLabel htmlFor="IMEI" className="custom-form-label">
                      Guarantor IMEI NO.<span className="red-star">{` `}*</span>
                    </CFormLabel>
                    <CFormInput
                      type="number"
                      id="imei2"
                      placeholder="Enter Your IMEI Number "
                      name="imei2"
                      value={imei2}
                      onChange={onChangeValue}
                      required
                      className="custom-form-input"
                    />
                  </CCol>
                </>
              )}
            </CRow>
            {showEmiTable && emiResult.schedule.length > 0 && (
              <CCol xs={12} className="pt-20">
                <CTable bordered responsive hover>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Month</CTableHeaderCell>
                      <CTableHeaderCell>Opening (₹)</CTableHeaderCell>
                      <CTableHeaderCell>EMI (₹)</CTableHeaderCell>
                      <CTableHeaderCell>Interest (₹)</CTableHeaderCell>
                      <CTableHeaderCell>Principal (₹)</CTableHeaderCell>
                      <CTableHeaderCell>Closing (₹)</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>

                  <CTableBody>
                    {emiResult.schedule.map((row) => (
                      <CTableRow key={row.month}>
                        <CTableDataCell>{row.month}</CTableDataCell>
                        <CTableDataCell>{row.opening}</CTableDataCell>
                        <CTableDataCell>{row.emi}</CTableDataCell>
                        <CTableDataCell>{row.interest}</CTableDataCell>
                        <CTableDataCell>{row.principal}</CTableDataCell>
                        <CTableDataCell>{row.closing}</CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </CCol>
            )}

            <div className="identification_btn">
              <CCol xs={12} className="pt-20 d-flex gap-2">
                <CButton
                  type="button"
                  color="info"
                  disabled={
                    !selectedProduct ||
                    !mainData.loanTerm ||
                    toNumber(mainData.loanAmountRequsted) <= 0 ||
                    selectedAnnualRate <= 0
                  }
                  onClick={handleShowEmi}
                >
                  Show EMI
                </CButton>
                {showEmiTable && (
                  <div className="pt-2">
                    <strong>Monthly EMI:</strong> ₹{emiResult.emi}
                    {"  "}
                    <span className="ms-3">
                      <strong>Rate:</strong> {selectedAnnualRate}% p.a.
                    </span>
                    <span className="ms-3">
                      <strong>Tenure:</strong> {mainData.loanTerm} months
                    </span>
                  </div>
                )}
              </CCol>
              <CButton type="submit" color="primary" className="px-4">
                Save
              </CButton>
            </div>

          </CForm>
        </CContainer>
      </CCardBody>
    </>
  );
};
RegistrationForm.propTypes = {
  formData: PropTypes.object,
  customer: PropTypes.object,
  updateFormData: PropTypes.func,
  setKey: PropTypes.func,
};
export default RegistrationForm;